import { useState, useEffect, useCallback } from 'react';
import {
  EventLogEntry,
  getEventLog,
  getGroupsByOrg,
  Group,
  getRecordingDownloadUrl,
} from '../../services/admin';

const PAGE_SIZE = 50;

const EVENT_TYPES = [
  'RADIO_CONNECTED',
  'RADIO_DISCONNECTED',
  'RADIO_REGISTERED',
  'PTT_STARTED',
  'PTT_ENDED',
  'PTT_BUSY',
  'SOS_ACTIVATED',
  'AUTHENTICATION_FAILED',
  'REGISTRATION_FAILED',
  'RECORDING_FAILED',
  'RECORDING_COMPLETED',
];

const severityStyle: Record<string, string> = {
  information: 'text-blue-300 bg-blue-900/20 border-blue-800',
  warning: 'text-yellow-300 bg-yellow-900/20 border-yellow-800',
  emergency: 'text-red-300 bg-red-900/30 border-red-600',
  error: 'text-orange-300 bg-orange-900/20 border-orange-700',
};

const eventIcon = (eventType: string): string => {
  if (eventType.startsWith('SOS')) return '🚨';
  if (eventType.startsWith('PTT')) return '🎙️';
  if (eventType.startsWith('RADIO')) return '📡';
  if (eventType.startsWith('RECORDING')) return '💾';
  return '•';
};

/** Present a human-readable summary line for an event, per event type. */
const describeEvent = (e: EventLogEntry, groupName: (id?: string | null) => string): string => {
  const group = groupName(e.talk_group_id);
  const radio = e.radio_id ? e.radio_id.substring(0, 8) : 'unknown radio';
  switch (e.event_type) {
    case 'PTT_STARTED':
      return `PTT started — ${radio} on ${group}`;
    case 'PTT_ENDED': {
      const ms = (e.metadata?.duration_ms as number) ?? undefined;
      return `PTT ended — ${radio} on ${group}${ms ? ` (${(ms / 1000).toFixed(1)}s)` : ''}`;
    }
    case 'PTT_BUSY':
      return `PTT denied (talk group busy) — ${radio} on ${group}`;
    case 'RADIO_CONNECTED':
      return `Radio connected — ${radio}`;
    case 'RADIO_DISCONNECTED':
      return `Radio disconnected — ${radio}`;
    case 'RADIO_REGISTERED':
      return `Radio registered — ${(e.metadata?.model as string) ?? radio}`;
    case 'SOS_ACTIVATED':
      return `SOS ACTIVATED — ${radio}`;
    case 'AUTHENTICATION_FAILED':
      return `Authentication failed — ${radio} (${(e.metadata?.reason as string) ?? 'unknown reason'})`;
    case 'REGISTRATION_FAILED':
      return `Registration failed`;
    case 'RECORDING_FAILED':
      return `Recording failed — ${(e.metadata?.reason as string) ?? 'unknown reason'}`;
    case 'RECORDING_COMPLETED':
      return `Recording completed — ${radio} on ${group}`;
    default:
      return e.event_type;
  }
};

export default function LogManager({ orgId }: { orgId: string }) {
  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Filters
  const [eventType, setEventType] = useState('');
  const [severity, setSeverity] = useState('');
  const [talkGroupId, setTalkGroupId] = useState('');
  const [radioId, setRadioId] = useState('');
  const [hasRecording, setHasRecording] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const groupName = useCallback(
    (id?: string | null) => groups.find((g) => g.id === id)?.name ?? (id ? id.substring(0, 8) : 'unknown group'),
    [groups]
  );

  const load = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        setError(null);
        const data = await getEventLog(orgId, {
          event_type: eventType || undefined,
          severity: severity || undefined,
          talk_group_id: talkGroupId || undefined,
          radio_id: radioId || undefined,
          has_recording: hasRecording || undefined,
          start: start ? new Date(start).toISOString() : undefined,
          end: end ? new Date(end).toISOString() : undefined,
          limit: PAGE_SIZE,
          offset,
        });
        setEvents(data.events);
        setTotal(data.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load event log');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [orgId, eventType, severity, talkGroupId, radioId, hasRecording, start, end, offset]
  );

  useEffect(() => {
    if (!orgId) return;
    getGroupsByOrg(orgId).then(setGroups).catch(() => setGroups([]));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    load();
    const interval = setInterval(() => load(true), 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, eventType, severity, talkGroupId, radioId, hasRecording, start, end, offset]);

  // Any filter change resets to the first page.
  useEffect(() => {
    setOffset(0);
  }, [eventType, severity, talkGroupId, radioId, hasRecording, start, end]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">📋 Operational Log</h2>
        <button
          onClick={() => load()}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg p-3">{error}</div>
      )}

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Event type</label>
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm"
          >
            <option value="">All events</option>
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Severity</label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm"
          >
            <option value="">All severities</option>
            <option value="information">Information</option>
            <option value="warning">Warning</option>
            <option value="emergency">Emergency</option>
            <option value="error">Error</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Talk group</label>
          <select
            value={talkGroupId}
            onChange={(e) => setTalkGroupId(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm"
          >
            <option value="">All talk groups</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Radio ID</label>
          <input
            value={radioId}
            onChange={(e) => setRadioId(e.target.value)}
            placeholder="radio id"
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">From</label>
          <input
            type="datetime-local"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">To</label>
          <input
            type="datetime-local"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex items-end gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={hasRecording} onChange={(e) => setHasRecording(e.target.checked)} />
            Has recording
          </label>
        </div>
        <div className="flex items-end">
          <button
            onClick={() => {
              setEventType('');
              setSeverity('');
              setTalkGroupId('');
              setRadioId('');
              setHasRecording(false);
              setStart('');
              setEnd('');
            }}
            className="text-sm text-gray-400 hover:text-white underline"
          >
            Clear filters
          </button>
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">No events found</div>
      ) : (
        <div className="space-y-2">
          {events.map((e) => (
            <div
              key={e.id}
              className={`p-3 rounded-lg border ${severityStyle[e.severity] ?? 'text-gray-300 bg-gray-800 border-gray-700'}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="text-xl flex-shrink-0">{eventIcon(e.event_type)}</div>
                  <div className="min-w-0">
                    <p className="font-medium">{describeEvent(e, groupName)}</p>
                    <p className="text-xs opacity-70 mt-0.5">
                      {new Date(e.timestamp).toLocaleString()} · {e.event_type}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {e.recording_id && e.recording_status === 'completed' && (
                    <>
                      <audio controls preload="none" className="h-8" style={{ maxWidth: 220 }}>
                        <source src={getRecordingDownloadUrl(e.recording_id)} />
                      </audio>
                      <a
                        href={getRecordingDownloadUrl(e.recording_id)}
                        className="text-xs bg-black/30 hover:bg-black/50 px-2 py-1 rounded whitespace-nowrap"
                      >
                        Download
                      </a>
                    </>
                  )}
                  {e.recording_id && e.recording_status === 'expired' && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">Recording expired</span>
                  )}
                  {e.recording_id && e.recording_status && !['completed', 'expired'].includes(e.recording_status) && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">Recording {e.recording_status}</span>
                  )}
                  {e.metadata && Object.keys(e.metadata).length > 0 && (
                    <button
                      onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                      className="text-xs bg-black/20 hover:bg-black/40 px-2 py-1 rounded"
                    >
                      {expanded === e.id ? 'Hide details' : 'Details'}
                    </button>
                  )}
                </div>
              </div>
              {expanded === e.id && e.metadata && (
                <div className="mt-2 pt-2 border-t border-white/10 text-xs space-y-1">
                  {Object.entries(e.metadata).map(([k, v]) => (
                    <div key={k}>
                      <span className="opacity-60">{k.replace(/_/g, ' ')}:</span> {String(v)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center text-sm text-gray-400 pt-2 border-t border-gray-700">
        <span>
          Showing {events.length === 0 ? 0 : offset + 1}-{offset + events.length} of {total}
        </span>
        <div className="flex items-center gap-2">
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40"
          >
            ← Prev
          </button>
          <span>Page {currentPage} / {totalPages}</span>
          <button
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
